import { createElement } from 'react';
import { createTheme, type Theme } from '@mui/material/styles';

export type AdminMode = 'light' | 'dark';

export interface AdminTokens {
  primary: string;
  primaryDark: string;
  primarySoft: string;
  primaryBorder: string;
  onPrimary: string;
  text: string;
  textSecondary: string;
  textDisabled: string;
  bg: string;
  paper: string;
  divider: string;
  hover: string;
  selected: string;
  inverse: string;
  onInverse: string;
  success: string;
  successSoft: string;
  info: string;
  infoSoft: string;
  warning: string;
  warningSoft: string;
  error: string;
  errorSoft: string;
  switchTrack: string;
}

export const ADMIN_TOKENS: Record<AdminMode, AdminTokens> = {
  light: {
    primary: '#F75E4C',
    primaryDark: '#DC503C',
    primarySoft: '#FEEFED',
    primaryBorder: '#FCC9C2',
    onPrimary: '#FFFFFF',
    text: '#18181B',
    textSecondary: '#71717A',
    textDisabled: '#A1A1AA',
    bg: '#FAFAFA',
    paper: '#FFFFFF',
    divider: '#EDEDED',
    hover: '#F4F4F5',
    selected: '#EFEFF1',
    inverse: '#18181B',
    onInverse: '#FFFFFF',
    success: '#4CAF50',
    successSoft: '#EAF5EA',
    info: '#1976D2',
    infoSoft: '#E3EEF9',
    warning: '#FF9800',
    warningSoft: '#FFF3E0',
    error: '#D32F2F',
    errorSoft: '#FBE9E9',
    switchTrack: '#E2E8F0',
  },
  dark: {
    primary: '#F75E4C',
    primaryDark: '#DC503C',
    primarySoft: '#3A2522',
    primaryBorder: '#6B3A33',
    onPrimary: '#FFFFFF',
    text: '#FFFFFF',
    textSecondary: '#A1A1AA',
    textDisabled: '#6B6B73',
    bg: '#0F0F10',
    paper: '#18181B',
    divider: '#27272A',
    hover: '#222225',
    selected: '#2E2E32',
    inverse: '#FFFFFF',
    onInverse: '#18181B',
    success: '#66BB6A',
    successSoft: '#1F2D20',
    info: '#42A5F5',
    infoSoft: '#1C2A3A',
    warning: '#FFA726',
    warningSoft: '#3A2E1C',
    error: '#F44336',
    errorSoft: '#3A1F1F',
    switchTrack: '#374151',
  },
};

export const ADMIN_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Inter", "Segoe UI", Roboto, sans-serif';

export const ADMIN_SHADOW_XS = '0 1px 2px rgba(0,0,0,0.04)';
export const ADMIN_SHADOW_DIALOG = '0 12px 40px -8px rgba(0,0,0,0.18)';

export const ADMIN_TYPE = {
  statValue: { fontSize: 28, lineHeight: '34px', fontWeight: 600, letterSpacing: '-0.6px' },
  pageTitle: { fontSize: 20, lineHeight: '28px', fontWeight: 600, letterSpacing: '-0.3px' },
  dialogTitle: { fontSize: 16, lineHeight: '24px', fontWeight: 600, letterSpacing: '-0.1px' },
  sectionTitle: { fontSize: 15, lineHeight: '22px', fontWeight: 600, letterSpacing: '-0.1px' },
  cardTitle: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
  body2: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
  nav: { fontSize: 13, lineHeight: '20px', fontWeight: 500 },
  body: { fontSize: 13, lineHeight: '20px', fontWeight: 400 },
  strong: { fontSize: 13, lineHeight: '20px', fontWeight: 500 },
  button: { fontSize: 13, lineHeight: '20px', fontWeight: 500 },
  caption: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
  tableHeader: { fontSize: 12, lineHeight: '16px', fontWeight: 500 },
  pill: { fontSize: 12, lineHeight: '16px', fontWeight: 500 },
  groupLabel: { fontSize: 11, lineHeight: '16px', fontWeight: 500, letterSpacing: '0.2px' },
} as const;

const checkboxBox = (t: AdminTokens, state: 'off' | 'on' | 'mixed') => createElement(
  'span',
  {
    style: {
      width: 16, height: 16, borderRadius: 4, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: state === 'off' ? t.paper : t.primary,
      border: state === 'off' ? `1.25px solid ${t.textDisabled}` : 'none',
    },
  },
  state === 'off' ? null : createElement(
    'svg',
    { width: 12, height: 12, viewBox: '0 0 20 20', fill: 'none' },
    createElement('path', { d: state === 'on' ? 'M16.66 5L7.49 14.16L3.33 10' : 'M4.5 10H15.5', stroke: '#fff', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' }),
  ),
);

export const createAdminTheme = (mode: AdminMode): Theme => {
  const t = ADMIN_TOKENS[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, dark: t.primaryDark, light: t.primarySoft, contrastText: t.onPrimary },
      success: { main: t.success, light: t.successSoft },
      info: { main: t.info, light: t.infoSoft },
      warning: { main: t.warning, light: t.warningSoft },
      error: { main: t.error, light: t.errorSoft },
      text: { primary: t.text, secondary: t.textSecondary, disabled: t.textDisabled },
      background: { default: t.bg, paper: t.paper },
      divider: t.divider,
      action: {
        hover: t.hover,
        selected: t.selected,
        active: t.textSecondary,
        disabled: t.textDisabled,
        disabledBackground: t.hover,
        focus: t.selected,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: ADMIN_FONT,
      fontSize: 13,
      body1: ADMIN_TYPE.body2,
      body2: ADMIN_TYPE.body,
      caption: ADMIN_TYPE.caption,
      button: { ...ADMIN_TYPE.button, textTransform: 'none' },
    },
    components: {
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            lineHeight: '20px',
            padding: '8px 14px',
            minWidth: 0,
            gap: 6,
            '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 },
            '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
          },
          sizeSmall: { padding: '5px 10px' },
          contained: {
            backgroundColor: t.primary,
            color: t.onPrimary,
            '&:hover': { backgroundColor: t.primaryDark },
          },
          outlined: {
            backgroundColor: t.paper,
            color: t.text,
            borderColor: t.divider,
            boxShadow: ADMIN_SHADOW_XS,
            '&:hover': { backgroundColor: t.hover, borderColor: t.divider },
          },
          text: {
            color: t.text,
            '&:hover': { backgroundColor: t.hover },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            width: 32,
            height: 32,
            padding: 0,
            color: t.textSecondary,
            '&:hover': { backgroundColor: t.hover, color: t.text },
          },
          sizeSmall: { width: 28, height: 28 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: 14,
            backgroundColor: t.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.divider },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.textDisabled },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.primary, borderWidth: 1.5 },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.error },
          },
          input: { padding: '9px 12px', height: 20 },
          inputSizeSmall: { padding: '6px 10px' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: 13, fontWeight: 500, color: t.textSecondary, '&.Mui-focused': { color: t.primary } },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontSize: 12, marginLeft: 0, marginTop: 6 } },
      },
      MuiSelect: {
        styleOverrides: { select: { fontSize: 13 }, icon: { color: t.textSecondary } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${t.divider}`,
            boxShadow: ADMIN_SHADOW_DIALOG,
            marginTop: 4,
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            lineHeight: '20px',
            borderRadius: 6,
            padding: '7px 10px',
            minHeight: 0,
            gap: 10,
            '&:hover': { backgroundColor: t.hover },
            '&.Mui-selected': { backgroundColor: t.selected, '&:hover': { backgroundColor: t.selected } },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${t.divider}`, boxShadow: ADMIN_SHADOW_DIALOG },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 16, boxShadow: ADMIN_SHADOW_DIALOG, backgroundImage: 'none' },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(24,24,27,0.32)' } } },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { ...ADMIN_TYPE.dialogTitle, padding: '20px 24px 4px' } },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: '16px 24px 20px' } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '14px 24px', gap: 8, backgroundColor: t.bg, borderTop: `1px solid ${t.divider}`, '& > :not(:first-of-type)': { marginLeft: 0 } },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 36, height: 20, padding: 0 },
          switchBase: {
            padding: 2,
            '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff' },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: t.primary, opacity: 1 },
          },
          thumb: { width: 16, height: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.2)', backgroundColor: '#fff' },
          track: { borderRadius: 10, backgroundColor: t.switchTrack, opacity: 1 },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
          icon: checkboxBox(t, 'off'),
          checkedIcon: checkboxBox(t, 'on'),
          indeterminateIcon: checkboxBox(t, 'mixed'),
        },
        styleOverrides: {
          root: {
            padding: 0,
            width: 16,
            height: 16,
            color: t.textDisabled,
            '& .MuiSvgIcon-root': { fontSize: 20 },
            '&.Mui-checked': { color: t.primary },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.inverse, color: t.onInverse, fontSize: 12, borderRadius: 6, padding: '6px 8px' },
          arrow: { color: t.inverse },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.divider, fontSize: 13, padding: '10px 16px' },
          head: { ...ADMIN_TYPE.tableHeader, color: t.textSecondary, backgroundColor: t.bg },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontSize: 13, fontWeight: 500, minHeight: 40, minWidth: 0, padding: '10px 0', marginRight: 20, color: t.textSecondary, '&.Mui-selected': { color: t.text } },
        },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 40 }, indicator: { backgroundColor: t.primary, height: 2 } },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: t.divider } } },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 6, fontSize: 12, fontWeight: 500, height: 20 } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, fontSize: 13 } },
      },
    },
  });
};
